import { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";

// Opt-in nature ambience (forest birds & gentle stream). OFF by default, never autoplays.
// Loops softly and fades in/out — calm and safe for the patient population.
const SRC = "/audio/forest-ambience.mp3";
const TARGET_VOL = 0.32;

export function AmbientSound({ className = "" }) {
  const { t } = useLang();
  const [on, setOn] = useState(false);
  const audioRef = useRef(null);
  const fadeRef = useRef(null);

  useEffect(() => {
    const a = new Audio(SRC);
    a.loop = true;
    a.preload = "none";
    a.volume = 0;
    audioRef.current = a;
    return () => {
      clearInterval(fadeRef.current);
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const fadeTo = (target, done) => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    fadeRef.current = setInterval(() => {
      const step = 0.04;
      if (Math.abs(a.volume - target) <= step) {
        a.volume = target;
        clearInterval(fadeRef.current);
        done && done();
      } else {
        a.volume = Math.max(0, Math.min(1, a.volume + (target > a.volume ? step : -step)));
      }
    }, 90);
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (on) {
      fadeTo(0, () => a.pause());
      setOn(false);
    } else {
      try {
        a.volume = 0;
        await a.play();
        fadeTo(TARGET_VOL);
        setOn(true);
      } catch (e) {
        /* playback blocked — stay off */
      }
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid="ambient-toggle"
      aria-pressed={on}
      aria-label={on ? t("audio.disable") : t("audio.enable")}
      title={on ? t("audio.disable") : t("audio.enable")}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
        on ? "border-petrol bg-petrol text-white" : "border-petrol/45 text-ink-soft hover:border-petrol hover:text-petrol"
      } ${className}`}
    >
      {on ? <Volume2 className="h-4 w-4" strokeWidth={1.6} /> : <VolumeX className="h-4 w-4" strokeWidth={1.6} />}
    </button>
  );
}
